import { H1 } from "@/components/Heading";
import { H2B } from "@/components/Heading/H2B";
import { H3B } from "@/components/Heading/H3B";
import { IndigenousMailToLink } from "@/components/Link";
import { translations } from "@/constants/translations";
import { renderInnerHtml } from "@/helpers/dom";
import { useStore } from "@/store";
import { Button, Divider, HStack, Link, Stack, Text } from "@chakra-ui/react";
import React, { useEffect, useMemo, useState } from "react";
import { WelcomeFooter } from "../../Welcome/WelcomeFooter";
import { ProvinceSelect } from "./ProvinceSelect";
import { CommunitySelect } from "./CommunitySelect";
import { api } from "@/api";
import { AppLink } from "@/routing/AppLink";
import {
  provinceIdByFullName,
  type ProvinceFull,
} from "@/store/types/province";
import { OverdosePlanningCallout } from "@/components/OverdosePlanningCallout";
import { useApiQuery } from "@/api/hooks/useApiQuery";
import { telemetry } from "@/telemetry";

export const IndigenousPortalPage = () => {
  const { activeLanguage, setActiveProvinceId, setActiveIndigenousCommunity } =
    useStore();
  const [province, setProvince] = useState<string | undefined>();
  const [communityId, setCommunityId] = useState<number | undefined>();
  const language = translations.pages["indigenous-portal"][activeLanguage];
  const indigenousLanguage = translations.common.indigenous[activeLanguage];

  useEffect(() => {
    setActiveProvinceId(undefined);
  }, []);

  const provincesQuery = useApiQuery({
    queryKey: ["indigenous-communities"],
    queryFn: () => api.getIndigenousCommunities(),
  });

  const provinces = useMemo(
    () => provincesQuery.data || [],
    [provincesQuery.data],
  );

  const communitiesByProvince = useMemo(() => {
    return provinces.find((p) => p.province === province)?.communities || [];
  }, [provinces, province]);

  const communityStatusQuery = useApiQuery({
    queryKey: ["indigenous-community-status", communityId],
    queryFn: () => api.getIndigenousCommunityStatus(communityId!),
    enabled: communityId !== undefined,
  });

  const communityStatus = communityStatusQuery.data;

  useEffect(() => {
    if (communityStatus && communityId !== undefined)
      telemetry.trackEvent("indigenous_community_selected", {
        community_id: String(communityId),
        activated: communityStatus.activated,
      });
  }, [communityStatus, communityId]);

  const handleProvinceSelect = (province: string) => {
    setProvince(province);
    setActiveProvinceId(provinceIdByFullName[province as ProvinceFull]);
    setCommunityId(undefined);
    setActiveIndigenousCommunity(undefined);
  };

  const handleCommunitySelect = (communityId: number) => {
    setCommunityId(communityId);
    const community = communitiesByProvince.find((c) => c.id === communityId);

    if (community) setActiveIndigenousCommunity(community);
  };

  return (
    <Stack spacing="14">
      <Stack
        textAlign="center"
        spacing="14"
      >
        <H1>{language.heading}</H1>
        <H2B>{language.subheading}</H2B>

        <Stack
          spacing="8"
          justifyContent="center"
          alignItems="center"
          textAlign="left"
          sx={{
            ".select__control": {
              width: "320px",
            },
          }}
        >
          <HStack flexDirection={{ base: "column", md: "row" }}>
            <Text width="160px">{language.province_label}</Text>
            <ProvinceSelect
              items={provinces}
              isLoading={provincesQuery.isPending}
              onSelect={handleProvinceSelect}
              placeholder={language.province_placeholder}
              value={province}
            />
          </HStack>
          <HStack flexDirection={{ base: "column", md: "row" }}>
            <Text width="160px">{language.community_label}</Text>
            <CommunitySelect
              items={communitiesByProvince}
              onSelect={handleCommunitySelect}
              placeholder={language.community_placeholder}
              value={communityId}
            />
          </HStack>
        </Stack>

        {communityStatus && (
          <>
            {communityStatus.activated ? (
              <Link
                as={AppLink}
                to="/indigenous/signin"
              >
                <Button alignSelf="center">
                  {renderInnerHtml(language.button_signin_label)}
                </Button>
              </Link>
            ) : (
              <IndigenousMailToLink
                subject={indigenousLanguage.email_subject_activate}
                body={language.email_body}
              >
                <Button
                  alignSelf="center"
                  onClick={() =>
                    telemetry.trackEvent("indigenous_activate_clicked", {
                      community_id: String(communityId),
                    })
                  }
                >
                  {renderInnerHtml(language.button_activate_label)}
                </Button>
              </IndigenousMailToLink>
            )}
          </>
        )}

        <Divider />

        <H3B>
          {renderInnerHtml(language.contact_desc, [
            <IndigenousMailToLink
              key="email"
              subject={indigenousLanguage.email_subject_add_new}
            >
              <Text
                as="span"
                color="primary-light"
              >
                {indigenousLanguage.email}
              </Text>
            </IndigenousMailToLink>,
          ])}
        </H3B>
        <IndigenousMailToLink
          subject={indigenousLanguage.email_subject_add_new}
        >
          <Button alignSelf="center">
            {renderInnerHtml(indigenousLanguage.email_button_label)}
          </Button>
        </IndigenousMailToLink>
      </Stack>

      <Divider />

      <OverdosePlanningCallout />

      <Divider />

      <WelcomeFooter />
    </Stack>
  );
};
